import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { language, translateText } from "../../slices/languageSlice";
import { ErrorMessage, Field, Formik, Form } from "formik";
import * as Yup from "yup";
import Spinner from "../../components/spinner";
import { addNotification } from "../../slices/notificationSlice";
import { showErrorMessage, showSuccessMessage } from "../../common/notification";

export const AddNotification = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { languageList } = useSelector((state) => state.language);
    const [loadingTitle, setLoadingTitle] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState(false);
    const [autoTranslate, setAutoTranslate] = useState(true);
    const timeoutsRef = useRef({});

    const validationSchema = Yup.object().shape(
        languageList?.data?.reduce((schema, language) => {
            schema[`${language.code}-title`] = Yup.string().required(`${language.name} title is required`);
            schema[`${language.code}-message`] = Yup.string().required(`${language.name} message is required`);
            return schema;
        }, {})
    );

    const initialValues = languageList?.data?.reduce((acc, language) => {
        acc[`${language.code}-title`] = "";
        acc[`${language.code}-message`] = "";
        return acc;
    }, {});

    const handleSubmit = useCallback(
        async (values, { setSubmitting, resetForm, isSubmitting }) => {
            if (isSubmitting) return;
            try {
                const trimmedValues = Object.keys(values).reduce((acc, key) => {
                    acc[key] = values[key].trim();
                    return acc;
                }, {});
        
                const promises = [];
        
                for (const language of languageList?.data) {
                    const titleKey = `${language.code}-title`;
                    const messageKey = `${language.code}-message`;
                    if (trimmedValues[titleKey] && trimmedValues[messageKey]) {
                        const payload = {
                            language_id: language.id,
                            title: trimmedValues[titleKey],
                            message: trimmedValues[messageKey],
                        };
        
                        const notificationPromise = new Promise((resolve, reject) => {
                            dispatch(addNotification({ payload }))
                                .then((response) => {
                                    if (response.payload.code === 200) {
                                        resolve(response.payload);
                                    } else {
                                        reject(response.payload);
                                    }
                                });
                        });
        
                        promises.push(notificationPromise);
                    }
                }
        
                await Promise.all(promises);
        
                showSuccessMessage("Notifications for all languages have been added successfully!");
                navigate('/notification');
                resetForm();
            } catch (error) {
                console.error("Error adding notification:", error?.response?.data || error?.message);
                showErrorMessage("Failed to add all notifications. Please try again.");
            } finally {
                setSubmitting(false);
            }
        },
        [languageList, dispatch, navigate]
    );

    useEffect(() => {
        dispatch(language({ limit: 100, sort: "id:asc", filter: "active" }));
    }, [dispatch]);

    const handleTranslate = async (text, langCode, type, setFieldValue) => {
        if (type === "title") setLoadingTitle(true);
        if (type === "message") setLoadingMessage(true);
        try {
            const translatedTexts = await Promise.all(
                languageList.data.map(async (language) => {
                    if (language.code !== langCode) {
                        try {
                            const response = await dispatch(
                                translateText({ text: text.trim(), lang: language.code })
                            );
                            return { code: language.code, type, text: response.payload };
                        } catch {
                            return { code: language.code, type, text: "" };
                        }
                    }
                    return null;
                })
            );

            translatedTexts.forEach((item) => {
                if (item) {
                    setFieldValue(`${item.code}-${item.type}`, item.text);
                }
            });
        } finally {
            if (type === "title") setLoadingTitle(false);
            if (type === "message") setLoadingMessage(false);
        }
    };

    const handleInputChange = (e, langCode, type, setFieldValue) => {
        const value = e.target.value;
        setFieldValue(`${langCode}-${type}`, value);
        if (!autoTranslate) return;
        if (timeoutsRef.current[`${langCode}-${type}`]) {
            clearTimeout(timeoutsRef.current[`${langCode}-${type}`]);
        }
        timeoutsRef.current[`${langCode}-${type}`] = setTimeout(() => {
            handleTranslate(value, langCode, type, setFieldValue);
        }, 500);
    };

    if (!languageList?.data || languageList.data.length === 0) {
        return <Spinner />;
    }

    return (
        <>
            <Helmet>
                <title>LEA.TRAINING | Add Notifications</title>
            </Helmet>
            <div className="content-page">
                <button className="btn btn-sm mt-2" onClick={() => navigate(-1)}>
                    <i className="fe-arrow-left font-22 avatar-title text-secondary" />
                </button>
                <div className="col-md-12 col-xl-12 mt-1">
                    <div className="widget-rounded-circle card">
                        <div className="card-body">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                }}
                            >
                                <h3 className="text-info">Add Notification</h3>
                                <button
                                    className={`btn ${autoTranslate ? "btn-success" : "btn-secondary"}`}
                                    onClick={() => setAutoTranslate((prev) => !prev)}
                                >
                                    {autoTranslate ? "Disable Auto Translate" : "Enable Auto Translate"}
                                </button>
                            </div>
                            <div className="px-5">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                >
                                    {({ isSubmitting, submitCount, errors, setFieldValue, values }) => (
                                        <Form>
                                            {languageList?.data?.map((language) => (
                                                <div className="mb-3" key={language.code}>
                                                    <h4>{language.name}</h4>
                                                    <div className="row">
                                                        <div className="col-md-5">
                                                            <label
                                                                htmlFor={`${language.code}-title`}
                                                                className="form-label"
                                                            >
                                                                <sup style={{ color: "red" }}>*</sup> Title
                                                            </label>
                                                            <div style={{ position: "relative" }}>
                                                                <Field
                                                                    as="input"
                                                                    name={`${language.code}-title`}
                                                                    id={`${language.code}-title`}
                                                                    value={values[`${language.code}-title`]}
                                                                    className={`form-control mb-2 ${submitCount > 0 &&
                                                                        errors[`${language.code}-title`]
                                                                        ? "is-invalid"
                                                                        : ""
                                                                        }`}
                                                                    placeholder="Enter title"
                                                                    maxLength="255"
                                                                    onChange={(e) =>
                                                                        handleInputChange(e, language.code, "title", setFieldValue)
                                                                    }
                                                                />
                                                                {loadingTitle && (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: "50%",
                                                                            right: "10px",
                                                                            transform: "translateY(-50%)",
                                                                        }}
                                                                    >
                                                                        <Spinner />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <ErrorMessage
                                                                name={`${language.code}-title`}
                                                                component="div"
                                                                className="invalid-feedback"
                                                            />
                                                        </div>
                                                        <div className="col-md-6 offset-md-1">
                                                            <label
                                                                htmlFor={`${language.code}-message`}
                                                                className="form-label"
                                                            >
                                                                <sup style={{ color: "red" }}>*</sup> Message
                                                            </label>
                                                            <div style={{ position: "relative" }}>
                                                                <Field
                                                                    as="textarea"
                                                                    name={`${language.code}-message`}
                                                                    id={`${language.code}-message`}
                                                                    value={values[`${language.code}-message`]}
                                                                    className={`form-control ${submitCount > 0 &&
                                                                        errors[`${language.code}-message`]
                                                                        ? "is-invalid"
                                                                        : ""
                                                                        }`}
                                                                    placeholder="Enter message"
                                                                    maxLength="1000"
                                                                    onChange={(e) =>
                                                                        handleInputChange(e, language.code, "message", setFieldValue)
                                                                    }
                                                                />
                                                                {loadingMessage && (
                                                                    <div
                                                                        style={{
                                                                            position: "absolute",
                                                                            top: "50%",
                                                                            right: "10px",
                                                                            transform: "translateY(-50%)",
                                                                        }}
                                                                    >
                                                                        <Spinner />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <ErrorMessage
                                                                name={`${language.code}-message`}
                                                                component="div"
                                                                className="invalid-feedback"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="text-center">
                                                <button
                                                    className="btn btn-primary"
                                                    type="submit"
                                                    disabled={isSubmitting}
                                                >
                                                    Submit
                                                </button>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
